import React, { useState } from 'react';
import { PaystackButton } from './PaystackButton';
import { submitLead } from '../lib/leadCapture';
import { Loader2 } from 'lucide-react';
interface LeadCaptureFormProps {
  course: string;
  amount: number;
  payLabel?: string;
  onPaymentSuccess: (reference: string) => void;
  onPaymentClose?: () => void;
  onCancel: () => void;
}
export function LeadCaptureForm({
  course,
  amount,
  payLabel,
  onPaymentSuccess,
  onPaymentClose,
  onCancel
}: LeadCaptureFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [leadSaved, setLeadSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    // Basic email check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        course
      });
      setLeadSaved(true);
    } finally {
      setSubmitting(false);
    }
  };
  if (leadSaved) {
    return <div className="space-y-4" data-id="element-373">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-374">
          <p className="text-sm text-gray-700" data-id="element-375">
            Thanks, <span className="font-semibold" data-id="element-376">{name.split(' ')[0]}</span>.
            Click below to complete your payment for{' '}
            <span className="font-semibold" data-id="element-377">{course}</span>.
          </p>
        </div>
        <PaystackButton amount={amount} email={email} onSuccess={onPaymentSuccess} onClose={onPaymentClose} variant="primary" fullWidth data-id="element-378">
          {payLabel ?? `Pay ₦${amount.toLocaleString()}`}
        </PaystackButton>
        <button type="button" onClick={onCancel} className="w-full text-gray-600 text-sm hover:text-gray-800 transition-colors duration-200" data-id="element-379">
          Cancel
        </button>
      </div>;
  }
  return <form onSubmit={handleContinue} className="space-y-4" data-id="element-380">
      <div data-id="element-381">
        <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-382">
          Full Name
        </label>
        <input id="lead-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" autoComplete="name" required data-id="element-383" />
      </div>

      <div data-id="element-384">
        <label htmlFor="lead-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-385">
          Email
        </label>
        <input id="lead-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" autoComplete="email" required data-id="element-386" />
      </div>

      <div data-id="element-387">
        <label htmlFor="lead-phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-388">
          Phone Number
        </label>
        <input id="lead-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+234 800 000 0000" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" autoComplete="tel" required data-id="element-389" />
      </div>

      <div data-id="element-390">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-391">
          Course
        </label>
        <div className="w-full px-4 py-3 border-2 border-gray-100 bg-gray-50 rounded-lg text-gray-700" data-id="element-392">
          {course}
        </div>
      </div>

      {error && <p className="text-sm text-red-600" role="alert" data-id="element-393">
          {error}
        </p>}

      <button type="submit" disabled={submitting} className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-full hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 transform disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-394">
        {'Continue '}
      </button>

      <button type="button" onClick={onCancel} className="w-full text-gray-600 text-sm hover:text-gray-800 transition-colors duration-200" data-id="element-395">
        Cancel
      </button>

      <p className="text-xs text-gray-500 text-center" data-id="element-396">
        We use your details to follow up about your enrolment. We won't spam
        you.
      </p>
    </form>;
}